import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import AppliedFilterChip from "../BaseChipComponents/AppliedFilterChipBase";
import { useChipFiltersStore } from "../BaseChipComponents/chipUtils";
import { getQuestionResultLabelParts } from "./questionResultLabels";
import type { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";

export const QuestionResultChip = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);
    const valuesStore = useStore(ApplicationFilterValuesStore);

    const questionResults = applicationFiltersStore.questionResults ?? [];
    const selectedModuleIdsKey = questionResults
        .map((questionResult) => questionResult.licensedModuleId)
        .join("||");

    useEffect(() => {
        const moduleIds = selectedModuleIdsKey
            .split("||")
            .filter((moduleId) => moduleId.length > 0);
        if (moduleIds.length === 0) return;

        valuesStore.loadQuestionResultFilterableModules();
        Array.from(new Set(moduleIds)).forEach((moduleId) =>
            valuesStore.loadModuleDetails(moduleId),
        );
    }, [selectedModuleIdsKey, valuesStore]);

    // A row naming a module this viewer cannot see is stripped from the query by
    // stripInaccessibleFilters, and the surfaces that strip it say so with a
    // permissions notice. Drop its chip to match, so a shared filter never
    // advertises a selection that is not in effect. Before the module catalog
    // loads nothing is known to be inaccessible, so every row still renders.
    const appliedQuestionResults = questionResults.filter(
        (questionResult) =>
            questionResult.tagIds.length > 0 &&
            valuesStore.canUseQuestionResultModule(
                questionResult.licensedModuleId,
            ),
    );

    if (appliedQuestionResults.length === 0) return null;

    return appliedQuestionResults.map((questionResult) => {
        const { moduleLabel, questionLabel, tagLabels } =
            getQuestionResultLabelParts(questionResult, valuesStore);

        const display = `${moduleLabel} → ${questionLabel}: ${tagLabels.join(", ")}`;

        return (
            <AppliedFilterChip
                key={`${questionResult.licensedModuleId}::${questionResult.questionId}`}
                disabled={props.disabled}
                isApplicable={props.isApplicable}
                onRemove={() =>
                    applicationFiltersStore.setQuestionResults(
                        (
                            applicationFiltersStore.questionResults ?? []
                        ).filter(
                            (x) =>
                                !(
                                    x.licensedModuleId ===
                                        questionResult.licensedModuleId &&
                                    x.questionId === questionResult.questionId
                                ),
                        ),
                    )
                }
            >
                {display}
            </AppliedFilterChip>
        );
    });
});
